// src/components/admin/villa/VillaForm.jsx
import React, { useState, useEffect, forwardRef } from 'react';
import { useForm } from 'react-hook-form';
import { villaService, dropdownService } from '../../../services/villa';
import { FaImage, FaTrash, FaStar, FaUpload, FaCheck, FaTimes } from 'react-icons/fa';

// Modern Button Component
const Button = ({ children, type = 'button', variant = 'primary', loading = false, disabled = false, onClick, className = '', ...props }) => {
  const baseClass = 'inline-flex items-center justify-center px-6 py-3 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 transform hover:scale-105 active:scale-95 shadow-lg';
  
  const variants = {
    primary: 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white focus:ring-amber-500 shadow-amber-200',
    outline: 'border-2 border-gray-300 bg-white hover:bg-gray-50 text-gray-700 focus:ring-gray-500 shadow-sm',
    danger: 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white focus:ring-red-500 shadow-red-200'
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseClass} ${variants[variant]} ${className}`}
      {...props}
    >
      {loading && (
        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
      )}
      {children}
    </button>
  );
};

// Modern Input Component
const Input = forwardRef(({ label, error, required = false, className = '', type = 'text', ...props }, ref) => {
  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-semibold text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <input
        ref={ref}
        type={type}
        className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all shadow-sm ${error ? 'border-red-500 focus:ring-red-500' : ''} ${className}`}
        {...props}
      />
      {error && (
        <p className="text-sm text-red-600 mt-1 flex items-center">
          <FaTimes className="mr-1" />
          {error}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

// Modern Loading Component
const Loading = ({ size = 'md' }) => {
  const sizes = {
    sm: 'h-5 w-5',
    md: 'h-8 w-8',
    lg: 'h-12 w-12'
  };

  return (
    <div className="flex items-center justify-center">
      <div className={`${sizes[size]} border-4 border-amber-500 border-t-transparent rounded-full animate-spin`}></div>
    </div>
  );
};

// Modern Image Upload Component
const ImageUpload = ({ images = [], onChange, maxImages = 10 }) => {
  const [dragOver, setDragOver] = useState(false);

  const handleFileSelect = (files) => {
    const fileArray = Array.from(files);
    const newImages = fileArray.map(file => ({
      id: Date.now() + Math.random(),
      file: file,
      url: URL.createObjectURL(file),
      is_primary: images.length === 0
    }));

    const updatedImages = [...images, ...newImages].slice(0, maxImages);
    onChange(updatedImages);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const removeImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    if (updatedImages.length > 0 && images[index]?.is_primary) {
      updatedImages[0].is_primary = true;
    }
    onChange(updatedImages);
  };

  const setPrimary = (index) => {
    const updatedImages = images.map((img, i) => ({
      ...img,
      is_primary: i === index
    }));
    onChange(updatedImages);
  };

  return (
    <div className="space-y-4">
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
          dragOver ? 'border-amber-500 bg-amber-50' : 'border-gray-300 hover:border-amber-400 bg-white'
        }`}
      >
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
          id="image-upload"
        />
        <label htmlFor="image-upload" className="cursor-pointer">
          <div className="text-gray-600">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 mb-4">
              <FaUpload className="text-amber-600 text-2xl" />
            </div>
            <p className="text-base font-medium mb-1">
              <span className="text-amber-600 hover:text-amber-700">
                Click to upload
              </span> or drag & drop
            </p>
            <p className="text-sm text-gray-500">
              PNG, JPG, WebP up to 5MB ({images.length}/{maxImages})
            </p>
          </div>
        </label>
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div key={image.id || index} className="relative group">
              <div className="aspect-square rounded-xl overflow-hidden bg-gray-100 border-2 border-gray-200 shadow-sm">
                <img
                  src={image.url}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-200 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="flex flex-col space-y-2">
                  {!image.is_primary && (
                    <button
                      type="button"
                      onClick={() => setPrimary(index)}
                      className="px-3 py-2 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700 flex items-center justify-center"
                    >
                      <FaStar className="mr-1" />
                      Set Primary
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="px-3 py-2 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 flex items-center justify-center"
                  >
                    <FaTrash className="mr-1" />
                    Remove
                  </button>
                </div>
              </div>

              {image.is_primary && (
                <div className="absolute top-2 left-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs px-3 py-1 rounded-lg font-semibold flex items-center shadow-lg">
                  <FaStar className="mr-1" />
                  Primary
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main VillaForm Component
const VillaForm = ({ villa, onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [images, setImages] = useState([]);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [facilities, setFacilities] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
    reset,
    trigger
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      title: '',
      description: '',
      category_id: '',
      location_id: '',
      address: '',
      bedrooms: 1,
      bathrooms: 1,
      max_guests: 2,
      daily_price_idr: '',
      daily_price_usd: '',
      monthly_price_idr: '',
      monthly_price_usd: '',
      yearly_price_idr: '',
      yearly_price_usd: '',
      sale_price_idr: '',
      sale_price_usd: '',
      property_size_sqm: '',
      land_size_sqm: '',
      property_status: 'leasehold',
      pets_allowed: false,
      parking_type: 'no_parking',
      view_type: '',
      status: 'active',
      featured: false
    }
  });

  const watchedValues = watch();

  const loadDropdownData = async () => {
    setDataLoading(true);
    try {
      const dropdownData = await dropdownService.getAllDropdownData();
      
      setCategories(dropdownData.categories || []);
      setLocations(dropdownData.locations || []);
      setFacilities(dropdownData.facilities || []);

    } catch (error) {
      console.error('Error loading dropdown data:', error);
      
      try {
        const [categoriesData, locationsData, facilitiesData] = await Promise.all([
          dropdownService.getCategories().catch(() => []),
          dropdownService.getLocations().catch(() => []),
          dropdownService.getFacilities().catch(() => [])
        ]);

        setCategories(categoriesData || []);
        setLocations(locationsData || []);
        setFacilities(facilitiesData || []);

      } catch (fallbackError) {
        console.error('Fallback failed:', fallbackError);
        setCategories([]);
        setLocations([]);
        setFacilities([]);
      }
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    loadDropdownData();
  }, []);

  useEffect(() => {
    if (villa && !dataLoading && categories.length > 0) {
      Object.keys(villa).forEach(key => {
        if (key !== 'images' && key !== 'facilities' && villa[key] !== null && villa[key] !== undefined) {
          setValue(key, villa[key], { shouldValidate: true, shouldDirty: true });
        }
      });

      if (villa.images && Array.isArray(villa.images)) {
        const formattedImages = villa.images.map(img => ({
          id: img.id,
          url: img.image_path || img.url,
          is_primary: img.is_primary || false,
          file: null
        }));
        setImages(formattedImages);
      }

      if (villa.facilities) {
        let facilityIds = [];
        if (Array.isArray(villa.facilities)) {
          facilityIds = villa.facilities.map(f => {
            if (typeof f === 'object' && f.id) {
              return parseInt(f.id);
            }
            return parseInt(f);
          });
        } else if (typeof villa.facilities === 'string') {
          try {
            const parsed = JSON.parse(villa.facilities);
            facilityIds = Array.isArray(parsed) ? parsed.map(f => parseInt(f)) : [];
          } catch (e) {
            console.error('Error parsing facilities JSON:', e);
            facilityIds = [];
          }
        }
        setSelectedFacilities(facilityIds);
      }

      setTimeout(() => {
        trigger();
      }, 100);
    }
  }, [villa, setValue, dataLoading, categories.length, trigger]);

  const calculateUSD = (idrValue) => {
    if (!idrValue || idrValue === '') return '';
    const numericValue = parseFloat(idrValue.toString().replace(/[^\d]/g, ''));
    if (isNaN(numericValue)) return '';
    return (numericValue / 16000).toFixed(2);
  };

  const dailyPriceIDR = watch('daily_price_idr');

  // Auto-calculate monthly, yearly, and sale prices based on daily price
  useEffect(() => {
    if (dailyPriceIDR) {
      const numericDaily = parseFloat(dailyPriceIDR.toString().replace(/[^\d]/g, ''));
      
      if (!isNaN(numericDaily) && numericDaily > 0) {
        // Calculate daily USD
        const dailyUSD = calculateUSD(numericDaily);
        setValue('daily_price_usd', dailyUSD, { shouldValidate: false });
        
        // Calculate monthly (daily * 30)
        const monthlyIDR = numericDaily * 30;
        const monthlyUSD = calculateUSD(monthlyIDR);
        setValue('monthly_price_idr', monthlyIDR, { shouldValidate: false });
        setValue('monthly_price_usd', monthlyUSD, { shouldValidate: false });
        
        // Calculate yearly (daily * 365)
        const yearlyIDR = numericDaily * 365;
        const yearlyUSD = calculateUSD(yearlyIDR);
        setValue('yearly_price_idr', yearlyIDR, { shouldValidate: false });
        setValue('yearly_price_usd', yearlyUSD, { shouldValidate: false });
        
        // Calculate sale price (yearly - 10% = yearly * 0.9)
        const saleIDR = yearlyIDR * 0.9;
        const saleUSD = calculateUSD(saleIDR);
        setValue('sale_price_idr', saleIDR, { shouldValidate: false });
        setValue('sale_price_usd', saleUSD, { shouldValidate: false });
      } else {
        // Clear all prices if daily price is empty or invalid
        setValue('daily_price_usd', '', { shouldValidate: false });
        setValue('monthly_price_idr', '', { shouldValidate: false });
        setValue('monthly_price_usd', '', { shouldValidate: false });
        setValue('yearly_price_idr', '', { shouldValidate: false });
        setValue('yearly_price_usd', '', { shouldValidate: false });
        setValue('sale_price_idr', '', { shouldValidate: false });
        setValue('sale_price_usd', '', { shouldValidate: false });
      }
    }
  }, [dailyPriceIDR, setValue]);

  const onSubmit = async (data) => {
    // Validation checks
    if (!data.title || data.title.trim() === '') {
      console.error('Villa title is required');
      return;
    }
    if (!data.category_id || data.category_id === '') {
      console.error('Category is required');
      return;
    }
    if (!data.location_id || data.location_id === '') {
      console.error('Location is required');
      return;
    }
    if (!data.description || data.description.trim() === '') {
      console.error('Description is required');
      return;
    }
    if (!data.address || data.address.trim() === '') {
      console.error('Address is required');
      return;
    }
    if (images.length === 0) {
      console.error('At least one image must be uploaded');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      
      Object.keys(data).forEach(key => {
        if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
          formData.append(key, data[key]);
        }
      });
      
      if (selectedFacilities && Array.isArray(selectedFacilities) && selectedFacilities.length > 0) {
        const validFacilities = selectedFacilities
          .filter(id => {
            const numId = parseInt(id);
            return !isNaN(numId) && numId > 0;
          })
          .map(id => parseInt(id));
        
        if (validFacilities.length > 0) {
          formData.append('facilities', JSON.stringify(validFacilities));
        } else {
          formData.append('facilities', JSON.stringify([]));
        }
      } else {
        formData.append('facilities', JSON.stringify([]));
      }
      
      const newImages = images.filter(img => img.file);
      newImages.forEach((img) => {
        formData.append('images', img.file);
      });
      
      if (villa) {
        const existingImages = images.filter(img => !img.file).map(img => ({
          id: img.id,
          image_path: img.url,
          is_primary: img.is_primary
        }));
        formData.append('existing_images', JSON.stringify(existingImages));
      }
      
      let response;
      if (villa) {
        response = await villaService.update(villa.id, formData);
      } else {
        response = await villaService.create(formData);
      }
      
      if (response) {
        // REMOVED: addNotification toast
        // Just call parent's onSuccess which will show SweetAlert
        if (onSuccess) onSuccess();
      } else {
        console.error('Failed to save villa');
      }
      
    } catch (error) {
      console.error('Error saving villa:', error);
      
      let errorMessage = 'An error occurred while saving villa';
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 400) {
          errorMessage = data.message || 'Invalid data sent';
          
          if (data.errors && Array.isArray(data.errors)) {
            const errorMessages = data.errors.map(err => err.msg || err.message).join(', ');
            errorMessage += `: ${errorMessages}`;
          }
        } else if (status === 500) {
          errorMessage = 'Server error occurred';
        } else {
          errorMessage = data.message || `Error ${status}: ${error.response.statusText}`;
        }
      } else if (error.request) {
        errorMessage = 'Cannot connect to server';
      } else {
        errorMessage = error.message || errorMessage;
      }
      
      // REMOVED: addNotification error toast
      console.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (newImages) => {
    setImages(newImages);
  };

  const handleFacilityChange = (facilityId) => {
    const numericFacilityId = parseInt(facilityId);
    setSelectedFacilities(prev => {
      const newSelection = prev.includes(numericFacilityId)
        ? prev.filter(id => id !== numericFacilityId)
        : [...prev, numericFacilityId];
      return newSelection;
    });
  };

  const retryLoadData = () => {
    loadDropdownData();
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    const numericValue = parseFloat(value.toString().replace(/[^\d]/g, ''));
    if (isNaN(numericValue)) return '';
    return new Intl.NumberFormat('id-ID').format(numericValue);
  };

  const renderSelect = (label, name, options, required = false, placeholder = null) => (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        {...register(name, required ? { required: `${label} is required` } : {})}
        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white text-gray-900 transition-all shadow-sm hover:border-amber-400"
        disabled={dataLoading}
      >
        <option value="">{placeholder || `Select ${label}`}</option>
        {options && options.length > 0 ? (
          options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name || option.area_name}
            </option>
          ))
        ) : (
          <option value="" disabled>
            {dataLoading ? `Loading ${label.toLowerCase()}...` : `No ${label.toLowerCase()} available`}
          </option>
        )}
      </select>
      {errors[name] && (
        <p className="text-sm text-red-600 mt-1 flex items-center">
          <FaTimes className="mr-1" />
          {errors[name].message}
        </p>
      )}
    </div>
  );

  const canSubmit = () => {
    const hasRequiredFields = watchedValues.title && 
                             watchedValues.category_id && 
                             watchedValues.location_id && 
                             watchedValues.description && 
                             watchedValues.address;
    const hasImages = images.length > 0;
    return hasRequiredFields && hasImages && !loading && !dataLoading;
  };

  if (dataLoading) {
    return (
      <div className="max-w-5xl mx-auto p-8 bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50">
        <div className="flex flex-col items-center justify-center h-64 space-y-6">
          <Loading size="lg" />
          <span className="text-lg font-medium text-gray-700">Loading form data from server...</span>
          <Button 
            variant="outline" 
            onClick={retryLoadData}
          >
            Retry Loading
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-8 bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        
        {/* Basic Information */}
        <div className="space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b-2 border-amber-200">
            <div className="p-2 bg-amber-100 rounded-lg">
              <FaCheck className="text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Basic Information</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Input
                label="Villa Title"
                {...register('title', { 
                  required: 'Villa title is required',
                  maxLength: { value: 80, message: 'Title maximum 80 characters' }
                })}
                error={errors.title?.message}
                placeholder="Example: Luxury Villa with Ocean View"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                {...register('description', { required: 'Description is required' })}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-vertical shadow-sm transition-all"
                placeholder="Describe the villa in detail..."
              />
              {errors.description && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <FaTimes className="mr-1" />
                  {errors.description.message}
                </p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Address <span className="text-red-500">*</span>
              </label>
              <textarea
                {...register('address', { required: 'Address is required' })}
                rows={2}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-vertical shadow-sm transition-all"
                placeholder="Full villa address..."
              />
              {errors.address && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <FaTimes className="mr-1" />
                  {errors.address.message}
                </p>
              )}
            </div>

            {renderSelect('Category', 'category_id', categories, true)}
            {renderSelect('Location', 'location_id', locations, true)}
          </div>
        </div>

        {/* Property Details */}
        <div className="space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b-2 border-amber-200">
            <div className="p-2 bg-amber-100 rounded-lg">
              <FaCheck className="text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Property Details</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Input
              label="Bedrooms"
              type="number"
              {...register('bedrooms', {
                required: 'Number of bedrooms is required',
                min: { value: 1, message: 'Minimum 1 bedroom' },
                valueAsNumber: true
              })}
              error={errors.bedrooms?.message}
              placeholder="2"
              min="1"
              required
            />

            <Input
              label="Bathrooms"
              type="number"
              {...register('bathrooms', {
                required: 'Number of bathrooms is required',
                min: { value: 1, message: 'Minimum 1 bathroom' },
                valueAsNumber: true
              })}
              error={errors.bathrooms?.message}
              placeholder="2"
              min="1"
              required
            />

            <Input
              label="Max Guests"
              type="number"
              {...register('max_guests', {
                min: { value: 1, message: 'Max guests must be more than 0' },
                valueAsNumber: true
              })}
              error={errors.max_guests?.message}
              placeholder="4"
              min="1"
            />

            <Input
              label="Building Size (m²)"
              type="number"
              {...register('property_size_sqm', {
                min: { value: 1, message: 'Building size must be more than 0' },
                valueAsNumber: true
              })}
              error={errors.property_size_sqm?.message}
              placeholder="150"
              min="1"
            />

            <Input
              label="Land Size (m²)"
              type="number"
              {...register('land_size_sqm', {
                min: { value: 1, message: 'Land size must be more than 0' },
                valueAsNumber: true
              })}
              error={errors.land_size_sqm?.message}
              placeholder="200"
              min="1"
            />

            {renderSelect('Property Status', 'property_status', [
              { id: 'leasehold', name: 'Leasehold' },
              { id: 'freehold', name: 'Freehold' }
            ])}

            {renderSelect('Parking Type', 'parking_type', [
              { id: 'no_parking', name: 'No Parking' },
              { id: 'street_parking', name: 'Street Parking' },
              { id: 'private_parking', name: 'Private Parking' }
            ])}

            {renderSelect('View Type', 'view_type', [
              { id: 'Jungle View', name: 'Jungle View' },
              { id: 'Rice Fields View', name: 'Rice Fields View' },
              { id: 'Beach View', name: 'Beach View' },
              { id: 'Mountain View', name: 'Mountain View' }
            ])}

            <div className="flex items-center space-x-3 pt-8">
              <input
                type="checkbox"
                {...register('pets_allowed')}
                className="h-5 w-5 text-amber-600 focus:ring-amber-500 border-gray-300 rounded cursor-pointer"
              />
              <label className="text-sm font-medium text-gray-900 select-none cursor-pointer">
                Pets Allowed
              </label>
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div className="space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b-2 border-amber-200">
            <div className="p-2 bg-amber-100 rounded-lg">
              <FaCheck className="text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Property Pricing</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Daily Price - EDITABLE */}
            <div className="space-y-4 p-5 bg-gradient-to-br from-amber-50 to-white rounded-xl border-2 border-amber-400">
              <h4 className="text-base font-bold text-gray-800 flex items-center gap-2">
                Daily Rate <span className="text-xs font-normal text-amber-600">(Enter price here)</span>
              </h4>
              <div className="grid grid-cols-1 gap-4">
                <Input
                  label="Daily Rate (IDR)"
                  type="number"
                  {...register('daily_price_idr', {
                    min: { value: 0, message: 'Price cannot be negative' },
                    valueAsNumber: true
                  })}
                  error={errors.daily_price_idr?.message}
                  placeholder="500000"
                  min="0"
                />
                <Input
                  label="Daily Rate (USD)"
                  type="number"
                  step="0.01"
                  {...register('daily_price_usd')}
                  error={errors.daily_price_usd?.message}
                  placeholder="Auto calculated"
                  min="0"
                  readOnly
                  className="bg-gray-50"
                />
              </div>
            </div>

            {/* Monthly Price - AUTO CALCULATED */}
            <div className="space-y-4 p-5 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-200">
              <h4 className="text-base font-bold text-gray-800 flex items-center gap-2">
                Monthly Rate <span className="text-xs font-normal text-blue-600">(Auto: Daily × 30)</span>
              </h4>
              <div className="grid grid-cols-1 gap-4">
                <Input
                  label="Monthly Rate (IDR)"
                  type="number"
                  {...register('monthly_price_idr')}
                  error={errors.monthly_price_idr?.message}
                  placeholder="Auto calculated"
                  min="0"
                  readOnly
                  className="bg-gray-50"
                />
                <Input
                  label="Monthly Rate (USD)"
                  type="number"
                  step="0.01"
                  {...register('monthly_price_usd')}
                  error={errors.monthly_price_usd?.message}
                  placeholder="Auto calculated"
                  min="0"
                  readOnly
                  className="bg-gray-50"
                />
              </div>
            </div>

            {/* Yearly Price - AUTO CALCULATED */}
            <div className="space-y-4 p-5 bg-gradient-to-br from-green-50 to-white rounded-xl border border-green-200">
              <h4 className="text-base font-bold text-gray-800 flex items-center gap-2">
                Yearly Rate <span className="text-xs font-normal text-green-600">(Auto: Daily × 365)</span>
              </h4>
              <div className="grid grid-cols-1 gap-4">
                <Input
                  label="Yearly Rate (IDR)"
                  type="number"
                  {...register('yearly_price_idr')}
                  error={errors.yearly_price_idr?.message}
                  placeholder="Auto calculated"
                  min="0"
                  readOnly
                  className="bg-gray-50"
                />
                <Input
                  label="Yearly Rate (USD)"
                  type="number"
                  step="0.01"
                  {...register('yearly_price_usd')}
                  error={errors.yearly_price_usd?.message}
                  placeholder="Auto calculated"
                  min="0"
                  readOnly
                  className="bg-gray-50"
                />
              </div>
            </div>

            {/* Sale Price - AUTO CALCULATED WITH DISCOUNT */}
            <div className="space-y-4 p-5 bg-gradient-to-br from-purple-50 to-white rounded-xl border border-purple-200">
              <h4 className="text-base font-bold text-gray-800 flex items-center gap-2">
                Sale Price <span className="text-xs font-normal text-purple-600">(Auto: Yearly - 10%)</span>
              </h4>
              <div className="grid grid-cols-1 gap-4">
                <Input
                  label="Sale Price (IDR)"
                  type="number"
                  {...register('sale_price_idr')}
                  error={errors.sale_price_idr?.message}
                  placeholder="Auto calculated"
                  min="0"
                  readOnly
                  className="bg-gray-50"
                />
                <Input
                  label="Sale Price (USD)"
                  type="number"
                  step="0.01"
                  {...register('sale_price_usd')}
                  error={errors.sale_price_usd?.message}
                  placeholder="Auto calculated"
                  min="0"
                  readOnly
                  className="bg-gray-50"
                />
              </div>
            </div>
          </div>
          
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <FaCheck className="text-amber-600 mt-1 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-bold mb-1">Auto-Calculation Notes:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>USD prices are automatically calculated with exchange rate 1 USD = 16,000 IDR</li>
                <li>Monthly Price = Daily Price × 30 days</li>
                <li>Yearly Price = Daily Price × 365 days</li>
                <li>Sale Price = Yearly Price - 10% (10% discount)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Status and Settings */}
        <div className="space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b-2 border-amber-200">
            <div className="p-2 bg-amber-100 rounded-lg">
              <FaCheck className="text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Status & Settings</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {renderSelect('Status', 'status', [
              { id: 'active', name: 'Active' },
              { id: 'inactive', name: 'Inactive' },
              { id: 'draft', name: 'Draft' }
            ], true)}

            <div className="flex items-center space-x-3 pt-8">
              <input
                type="checkbox"
                {...register('featured')}
                className="h-5 w-5 text-amber-600 focus:ring-amber-500 border-gray-300 rounded cursor-pointer"
              />
              <label className="text-sm font-medium text-gray-900 select-none cursor-pointer flex items-center gap-2">
                <FaStar className="text-amber-500" />
                Featured Villa (Display on homepage)
              </label>
            </div>
          </div>
        </div>

        {/* Facilities */}
        <div className="space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b-2 border-amber-200">
            <div className="p-2 bg-amber-100 rounded-lg">
              <FaCheck className="text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Villa Facilities</h3>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Select Available Facilities
            </label>
            {facilities && facilities.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-96 overflow-y-auto border-2 border-gray-200 rounded-xl p-5 bg-white shadow-inner">
                {facilities.map(facility => (
                  <label key={facility.id} className="flex items-center space-x-3 cursor-pointer hover:bg-amber-50 p-3 rounded-lg transition-all duration-200 border border-transparent hover:border-amber-200">
                    <input
                      type="checkbox"
                      checked={selectedFacilities.includes(facility.id)}
                      onChange={() => handleFacilityChange(facility.id)}
                      className="h-5 w-5 text-amber-600 focus:ring-amber-500 border-gray-300 rounded cursor-pointer"
                    />
                    <span className="text-sm font-medium text-gray-700 select-none flex-1">
                      {facility.name}
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 text-sm border-2 border-gray-200 rounded-xl p-6 text-center bg-gray-50">
                {dataLoading ? 'Loading facilities...' : 'No facilities available'}
                {!dataLoading && (
                  <button
                    type="button"
                    onClick={retryLoadData}
                    className="ml-2 text-amber-600 hover:text-amber-800 underline font-medium"
                  >
                    Retry
                  </button>
                )}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-3 flex items-center gap-2">
              <FaCheck className="text-amber-600" />
              Select facilities available at the villa ({selectedFacilities.length} facilities selected)
            </p>
          </div>
        </div>

        {/* Images */}
        <div className="space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b-2 border-amber-200">
            <div className="p-2 bg-amber-100 rounded-lg">
              <FaImage className="text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Villa Images</h3>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Upload Images <span className="text-red-500">*</span>
            </label>
            <ImageUpload
              images={images}
              onChange={handleImageChange}
              maxImages={10}
            />
            <p className="text-xs text-gray-500 mt-3 flex items-center gap-2">
              <FaImage className="text-amber-600" />
              Upload minimum 1 image, maximum 10 images. Format: JPG, PNG, WebP. Maximum size 5MB per image.
              The first image will automatically become the primary image.
            </p>
          </div>
        </div>

        {/* Form Summary */}
        <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-xl p-6 shadow-inner">
          <h4 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
            <FaCheck className="text-amber-600" />
            Form Summary:
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-700">
            <div className="flex items-start gap-2">
              <span className="text-amber-600 font-bold">•</span>
              <span><strong>Title:</strong> {watchedValues.title || 'Not filled'}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-amber-600 font-bold">•</span>
              <span><strong>Category:</strong> {categories.find(c => c.id == watchedValues.category_id)?.name || 'Not selected'}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-amber-600 font-bold">•</span>
              <span><strong>Location:</strong> {locations.find(l => l.id == watchedValues.location_id)?.area_name || 'Not selected'}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-amber-600 font-bold">•</span>
              <span><strong>Rooms:</strong> {watchedValues.bedrooms || 1} bedrooms, {watchedValues.bathrooms || 1} bathrooms</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-amber-600 font-bold">•</span>
              <span><strong>Guests:</strong> Maximum {watchedValues.max_guests || 2} people</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-amber-600 font-bold">•</span>
              <span><strong>Daily Rate:</strong> IDR {formatCurrency(watchedValues.daily_price_idr) || '0'} (${watchedValues.daily_price_usd || '0'})</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-amber-600 font-bold">•</span>
              <span><strong>Monthly Rate:</strong> IDR {formatCurrency(watchedValues.monthly_price_idr) || '0'}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-amber-600 font-bold">•</span>
              <span><strong>Yearly Rate:</strong> IDR {formatCurrency(watchedValues.yearly_price_idr) || '0'}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-amber-600 font-bold">•</span>
              <span><strong>Sale Price:</strong> IDR {formatCurrency(watchedValues.sale_price_idr) || '0'} (10% discount)</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-amber-600 font-bold">•</span>
              <span><strong>Facilities:</strong> {selectedFacilities.length} selected</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-amber-600 font-bold">•</span>
              <span><strong>Images:</strong> {images.length} images</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-amber-600 font-bold">•</span>
              <span><strong>Status:</strong> {watchedValues.status || 'active'}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-amber-600 font-bold">•</span>
              <span><strong>Featured:</strong> {watchedValues.featured ? 'Yes' : 'No'}</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-amber-600 font-bold">•</span>
              <span><strong>Pets:</strong> {watchedValues.pets_allowed ? 'Allowed' : 'Not Allowed'}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 border-t-2 border-gray-200">
          <div className="text-sm text-gray-600 font-medium">
            {villa ? (
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-lg">
                <FaCheck className="text-amber-600" />
                Edit Mode: Villa ID {villa.id}
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-lg">
                <FaCheck className="text-green-600" />
                Add New Villa Mode
              </span>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={!canSubmit()}
              className="w-full sm:w-auto"
            >
              {loading ? 'Saving...' : (villa ? 'Update Villa' : 'Save Villa')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default VillaForm;